import { assertBoolean, assertImList, assertImMap, assertObject, assertString } from '@Utils/assert';
import { fromJS } from 'immutable';
import { getNormalizedFilterThesaurusAsync } from '@Containers/ResultsFilters/ResultsFiltersService';
import { updateDialog } from '@Containers/Dialogs/DialogsActions';
import {
  updateThesaurus,
  updateThesaurusGroupById,
} from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersActions';
import {
  keyifyParamsForFiltersRequest,
  paramsForFilterGroups,
} from '@Containers/ResultsFilters/ResultsFiltersComponents/ListOfFilters/ListOfFiltersSelectors';
import { handleThesaurusItems } from '@Containers/ResultsFilters/performSearchFilterAsync/preparePropertiesUtils';
import { performSearchFilterAsync } from '@Containers/ResultsFilters/performSearchFilterAsync';
import { withGlobalSpinnerActionEnhancer } from '@Utils/Redux/withGlobalSpinnerActionEnhancer';
import { typeOfTreeViewDialog } from './TreeViewDialogConstants';

export function open(filterId, keyifyParamsForFiltersRequestArg) {
  assertString(filterId, 'filterId');
  assertString(keyifyParamsForFiltersRequestArg, 'keyifyParamsForFiltersRequest');

  return updateDialog({
    id: keyifyParamsForFiltersRequestArg + filterId,
    movable: false,
    expandSearchOptions: false,
    filterId,
    openCloseMap: {},
    dialogType: typeOfTreeViewDialog,
    keyifyParamsForFiltersRequest: keyifyParamsForFiltersRequestArg,
  });
}

export function toggleGroup(filterId, keyifyParamsForFiltersRequestArg, id, val, oldOpenCloseMap) {
  assertString(filterId, 'filterId');
  assertString(keyifyParamsForFiltersRequestArg, 'keyifyParamsForFiltersRequest');
  assertString(id, 'id');
  assertBoolean(val, 'val');
  assertObject(oldOpenCloseMap, 'oldOpenCloseMap');

  return updateDialog({
    id: keyifyParamsForFiltersRequestArg + filterId,
    dialogType: typeOfTreeViewDialog,
    openCloseMap: {
      ...oldOpenCloseMap,
      [id]: val,
    },
  });
}

export function toggleSearchOptions(filterId, keyifyParamsForFiltersRequestArg, val) {
  assertString(filterId, 'filterId');
  assertString(keyifyParamsForFiltersRequestArg, 'keyifyParamsForFiltersRequest');
  assertBoolean(val, 'val');

  return updateDialog({
    id: keyifyParamsForFiltersRequestArg + filterId,
    dialogType: typeOfTreeViewDialog,
    expandSearchOptions: val,
  });
}

export function createParamsForFetchChildren(filter, group, baseParams) {
  assertImMap(filter, 'filter');
  assertImMap(group, 'group');
  assertObject(baseParams, 'baseParams');

  const groupId = group.get('id');
  const value = filter.get('value');

  return {
    ...baseParams,
    properties: [
      {
        property: value,
        all: true,
        root: groupId,
      },
    ],
  };
}

export function createRootIdsForChildren(group) {
  const groupId = group.get('id');

  return group.get('rootIds').concat([groupId]).toArray();
}

export function fetchChildren(filter, group) {
  assertImMap(filter, 'filter');
  assertImMap(group, 'group');

  return async (dispatch, getState) => {
    const state = getState();
    const key = keyifyParamsForFiltersRequest(state);
    const baseParams = paramsForFilterGroups(state);
    const groupId = group.get('id');
    const selected = Boolean(group.get('selected'));
    const filterId = filter.get('id');
    const resultParams = createParamsForFetchChildren(filter, group, baseParams);

    const rootIds = createRootIdsForChildren(group);

    dispatch(
      updateThesaurusGroupById(key, filterId, groupId, {
        childrenRetrievalStatus: 'fetching',
      })
    );

    const result = await getNormalizedFilterThesaurusAsync(resultParams, {
      rootIds,
      selected,
    });

    dispatch(updateThesaurus(key, filterId, groupId, result));
  };
}

export const limitTo = withGlobalSpinnerActionEnhancer((filter, tags) => {
  assertImMap(filter, 'filter');
  assertImList(tags, 'tags');

  return async () => {
    try {
      const properties = handleThesaurusItems(filter, tags);

      await performSearchFilterAsync({
        filters: fromJS([filter]),
        properties,
      });
    } catch (e) {
      Logger.error(e);
    }
  };
});

export const exclude = withGlobalSpinnerActionEnhancer((filter, tags) => {
  assertImMap(filter, 'filter');
  assertImList(tags, 'tags');

  return async () => {
    try {
      const properties = handleThesaurusItems(filter, tags);

      await performSearchFilterAsync({
        filters: fromJS([filter]),
        properties,
        baseParamsExtension: { exclude: true },
      });
    } catch (e) {
      Logger.error(e);
    }
  };
});
